'use client';

import { useTranslation } from 'react-i18next';

import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';

import type { AvailableClassData } from '../_lib/server/server-actions';
import { ClassCard } from './class-card';

interface ClassSelectionProps {
  classes: AvailableClassData[];
  selectedClass: string;
  onClassSelect: (
    bookingId: string,
    price: number,
    templateId: string | null,
    isFull?: boolean,
  ) => void;
  selectedDate: Date;
  isLoading?: boolean;
}

export function ClassSelection({
  classes,
  selectedClass,
  onClassSelect,
  selectedDate,
  isLoading = false,
}: ClassSelectionProps) {
  const { t } = useTranslation('bookings');
  const isNativeLayout = useNativeLayout();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            style={
              isNativeLayout
                ? {
                    minHeight: 116,
                    borderRadius: 'var(--native-booking-radius)',
                  }
                : undefined
            }
            className="bg-muted/30 h-20 animate-pulse rounded-lg border"
          />
        ))}
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="bg-muted/30 rounded-lg border p-6 text-center">
        <p className="text-muted-foreground">{t('noClassesAvailable')}</p>
      </div>
    );
  }

  return (
    <div className="-m-1 space-y-2 p-1">
      {classes.map((classData) => (
        <ClassCard
          key={classData.booking_id}
          classData={classData}
          isSelected={selectedClass === classData.booking_id}
          onSelect={() =>
            onClassSelect(
              classData.booking_id,
              classData.price_per_person,
              classData.template_id,
              classData.is_full || classData.virtually_full,
            )
          }
          selectedDate={selectedDate}
        />
      ))}
    </div>
  );
}
